import React, { useState, useRef } from 'react';
import InfoTracking from '@app/components/common/InfoTracking';
import { ExclamationCircleFilled, SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Divider, Empty, Table, Button, Col, Tag, Drawer, Form, Input, Row, Select, Tooltip, Space, message, Modal, Typography } from 'antd';
import { insert, update, dropDetail, getDetailsById, getById } from '../../../../apis/PeriodosApi';
import Highlighter from "react-highlight-words";
import Details from './Details'
import {status_period} from '@app/constants/period';
import moment from 'moment';

const { Title } = Typography;
const { Option } = Select;
const { confirm } = Modal;
const App = ({ title, id, open, finish }) => {
  const [form] = Form.useForm()
  const [tipo, setTipo] = useState("");
  const [ID, setID] = useState(id);
  const [showModal, setShowModal] = useState(false);
  const [dataDetails, setDataDetails] = useState([]);
  const [anioFilter, setAnioFilter] = useState(null);
  const [openNewDetails, setOpenNewDetails] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [initialValues, setInitialValues] = useState(null);
  const searchInput = useRef(null);

  const anioOptions = React.useMemo(() => {
    const years = [...new Set((dataDetails || []).map(item => item?.anio).filter(Boolean))];
    return years
      .sort((a, b) => Number(a) - Number(b))
      .map((year) => ({ label: year.toString(), value: year.toString() }));
  }, [dataDetails]);

  const filteredDetails = React.useMemo(() => {
    if (!anioFilter) {
      return dataDetails;
    }

    return (dataDetails || []).filter(item => item?.anio?.toString() === anioFilter.toString());
  }, [dataDetails, anioFilter]);

  const statusLabelMap = React.useMemo(() => {
    return status_period.list.reduce((accumulator, item) => {
      accumulator[item.value] = item.text;
      return accumulator;
    }, {});
  }, []);

  const getStatusTagColor = (status) => {
    if (status == status_period.activo) {
      return 'green';
    }
    if (status == status_period.sin_calcular) {
      return 'gray';
    }
    if (status == status_period.calculado_parcial) {
      return 'green';
    }
    if (status == status_period.calculado_total) {
      return 'green';
    }
    if (status == status_period.cerrado) {
      return 'blue';
    }
    if (status == status_period.timbrado_parcial) {
      return 'purple';
    }
    if (status == status_period.timbrado_total) {
      return 'purple';
    }

    return 'default';
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (!values) return;
    } catch (errorInfo) {
      console.log('Failed:', errorInfo);
      return;
    }

    const fields = form.getFieldsValue()
    let success = false;
    if (ID > 0) {
      //Update
      const payload = {
        id: ID,
        ...fields
      }
      const response = await update(payload);
      success = response.ok;
      if (response.ok) {
        message.success('Se actualizó correctamente el periodo!');
      }
    } else {
      //Create
      const payload = {
        id: ID,
        ...fields
      }
      const response = await insert(payload);
      success = response.ok;
      if (response.ok) {
        setID(response.payload);
        message.success('Se guardo correctamente el periodo!');
      }
    }

    if (success) {
      finish(true, true);
    }
  }

  const onClickNewDetail = () => {
    setOpenNewDetails(true);
    setSelectedDetail(null);
  }

  const onClose = () => {
    setShowModal(false);
    finish(false);
    setInitialValues(null);
    setAnioFilter(null);
  }

  const finishDetail = async () => {
    setOpenNewDetails(false);
    setAnioFilter(null);
    await refreshDetails();
  }

  React.useEffect(() => {
    setShowModal(open);
  }, [open]);

  const effectRan = useRef(false);
  React.useEffect(() => {

    if (!effectRan.current) {
      if (ID > 0) {
        refreshData();
      } else {
        setInitialValues({
          descripcion: '',
          clave_sat: '',
        });
      }
    }

    return () => effectRan.current = true;
  }, [ID]);

  const refreshData = async () => {
    await refreshPeriodo();
    await refreshDetails();
  }

  const refreshPeriodo = async () => {
    const response = await getById(ID);
    if (response.ok) {
      let periodo = response.payload;

      // Convertir strings de fecha a objetos moment
      if (periodo.fecha_inicial) {
        periodo.fecha_inicial = moment(periodo.fecha_inicial);
      }
      if (periodo.fecha_final) {
        periodo.fecha_final = moment(periodo.fecha_final);
      }
      if (periodo.fecha_pago) {
        periodo.fecha_pago = moment(periodo.fecha_pago);
      }
      if (periodo.fecha_timbrado) {
        periodo.fecha_timbrado = moment(periodo.fecha_timbrado);
      }

      setInitialValues(periodo);
    }
  }

  const refreshDetails = async () => {
    const response = await getDetailsById(ID);
    if (response.ok) {
      let list = response.payload;

      // Convertir fechas en cada detalle
      list = list.map(item => ({
        ...item,
        fecha_inicial: item.fecha_inicial ? moment(item.fecha_inicial) : null,
        fecha_final: item.fecha_final ? moment(item.fecha_final) : null,
        fecha_pago: item.fecha_pago ? moment(item.fecha_pago) : null,
        fecha_timbrado: item.fecha_timbrado ? moment(item.fecha_timbrado) : null,
      }));

      setDataDetails(list);
    }
  }
  const deleteConfirm = async (item) => {
    confirm({
      title: '¿Confirmas en eliminar el detalle del periodo?',
      icon: <ExclamationCircleFilled />,
      content: 'No podrás recuperar su información',
      onOk: () => { deleteOk(item.id) },
      onCancel() {
        console.log('Cancel');
      },
    });
  }

  const deleteOk = async (id) => {
    const response = await dropDetail(id);
    if (response.ok) {
      message.success('¡El periodo ha sido eliminadó!');
      await refreshDetails();
    }
  }

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex, text) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Buscar ${text}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Limpiar
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            cerrar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) => {
      if (!record[dataIndex]) { return false; }
      return record[dataIndex].toString().toLowerCase().includes(value.toLowerCase());
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: 'Año',
      dataIndex: 'anio',
      key: 'anio',
      sorter: (a, b) => {
        return a.anio?.localeCompare(b.anio);
      },
      ...getColumnSearchProps("anio", "año"),
      render: (item, object) => {
        return <>
          <InfoTracking entity={'ConfiguracionPeriodoDetalle'} entityId={object.id} />
          <span>{object.anio}</span>
        </>
      }
    },
    {
      title: 'Número de periodo',
      dataIndex: 'num_periodo',
      key: 'num_periodo',
      sorter: (a, b) => {
        return a.anio?.localeCompare(b.num_periodo);
      },
      ...getColumnSearchProps("num_periodo", "Número de periodo"),
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      ...getColumnSearchProps("Descripción", "Descripción"),
    },
    {
      title: 'Días Periodo',
      dataIndex: 'dias_periodo',
      key: 'dias_periodo',
      ...getColumnSearchProps("dias_periodo", "Días Periodo"),
    },
    {
      title: 'Horas Periodo',
      dataIndex: 'horas_periodo',
      key: 'horas_periodo',
      ...getColumnSearchProps("horas_periodo", "Horas Periodo"),
    },
    {
      title: 'Mes',
      dataIndex: 'mes',
      key: 'mes',
      ...getColumnSearchProps("mes", "Mes"),
    },
    {
      title: 'Num Per del Mes',
      dataIndex: 'num_per_del_mes',
      key: 'num_per_del_mes',
      ...getColumnSearchProps("num_per_del_mes", "Num Per del Mes"),
    },
    {
      title: 'Total Per del Mes',
      dataIndex: 'total_per_del_mes',
      key: 'total_per_del_mes',
      ...getColumnSearchProps("total_per_del_mes", "Total Per del Mes"),
    },
    {
      title: 'Fecha Inicial',
      dataIndex: 'fecha_inicial',
      key: 'fecha_inicial',
      render: (value) => {
        if (value) {
          return <span>{moment(value).format('DD/MM/YYYY')}</span>
        } else {
          return <></>
        }
      }
    },
    {
      title: 'Fecha Final',
      dataIndex: 'fecha_final',
      key: 'fecha_final',
      render: (value) => {
        if (value) {
          return <span>{moment(value).format('DD/MM/YYYY')}</span>
        } else {
          return <></>
        }
      }
    },
    {
      title: 'Fecha Pago',
      dataIndex: 'fecha_pago',
      key: 'fecha_pago',
      render: (value) => {
        if (value) {
          return <span>{moment(value).format('DD/MM/YYYY')}</span>
        } else {
          return <></>
        }
      }
    },
    {
      title: 'Periodo Especial',
      dataIndex: 'periodo_especial',
      key: 'periodo_especial',
      render: (item, object) => {
        if (object?.periodo_especial) {
          return <Tag color="green">Sí</Tag>;
        } else {
          return <Tag color="red">No</Tag>;
        }
      }
    },
    {
      title: 'Estatus',
      dataIndex: 'estatus',
      key: 'estatus',
      render: (item, object) => {
        const status = object?.estatus;
        const label = statusLabelMap[status];

        if (!label) {
          return null;
        }

        return <Tag color={getStatusTagColor(status)}>{label}</Tag>;
      }
    },
    {
      title: '',
      dataIndex: 'actions',
      key: 'actions',
      render: (item, object) => {
        return <Space>
          <Button type='primary' size='small' onClick={() => {
            setOpenNewDetails(true);
            setSelectedDetail(object);
          }}>Editar</Button>
          <Button type='primary' size='small' danger onClick={() => {
            deleteConfirm(object);
          }}>Eliminar</Button>
        </Space>
      }
    }
  ];

  return (
    <Drawer
      title={<>
        {ID && <span><InfoTracking entity={'ConfiguracionPeriodo'} entityId={ID} /></span>}
        {title}
      </>}
      width={'100%'}
      placement={"right"}
      onClose={onClose}
      open={open}
      closable={true}
      height={'100%'}
      styles={{
        body: {
          paddingBottom: 80,
        },
      }}
      extra={[
        <Space>
          {ID && <span><InfoTracking entity={'ConfiguracionPeriodo'} entityId={ID} /></span>}
          <Button onClick={onClose}>Cancelar</Button>
          <Button onClick={handleOk} type="primary">
            Guardar
          </Button>
        </Space>
      ]}
    >
      <>
        {
          initialValues &&
          <Form layout="vertical" form={form} hideRequiredMark initialValues={initialValues} onFinish={handleOk}>
            <Row gutter={16}>
              <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                <Form.Item
                  name="tipo"
                  // label="Tipo"
                  label={
                    <div>
                      Tipo {" "}
                      <Tooltip title="Nombre variable periodo_tipo">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </div>
                  }
                  rules={[
                    {
                      required: true,
                      message: 'Por favor agrega un tipo de periodo',
                    },
                  ]}
                >
                  <Input placeholder="Por favor agrega un tipo de periodo" />
                  {/* <Select placeholder="Por favor selecciona un tipo">
                                {
                                    type_period.list.map((item)=><Option value={item.value}>{item.text}</Option>)
                                }
                            </Select> */}
                </Form.Item>
              </Col>
              <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                <Form.Item
                  name="descripcion"
                  label="Descripción"
                  rules={[
                    {
                      required: false,
                      message: 'Por favor agrega una descripción',
                    },
                  ]}
                >
                  <Input placeholder="Por favor agrega una descripción" />
                </Form.Item>
              </Col>
              <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                <Form.Item
                  name="clave_sat"
                  label="Clave SAT"
                  rules={[
                    {
                      required: true,
                      message: 'Por favor agrega la Clave SAT',
                    },
                  ]}
                >
                  <Input placeholder="Por favor agrega la Clave SAT" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        }

        <Divider></Divider>
        <div style={{ height: 60 }}>
          <div style={{ float: 'left' }}>
            <Title level={2}>Líneas de periodo</Title>
          </div>
          <span class="" style={{ float: 'right' }}>
            <Space align="center">
              <Select
                allowClear
                style={{ width: 180 }}
                placeholder="Filtrar por año"
                value={anioFilter}
                options={anioOptions}
                onChange={(value) => setAnioFilter(value || null)}
              />
              <Button disabled={!ID} onClick={onClickNewDetail} type="primary">Agregar nuevo detalle</Button>
            </Space>
          </span>
        </div>
        {
          openNewDetails &&
          <div style={{ marginBottom: 10 }}>
            {
              !selectedDetail &&
              <Details title="Agregar detalle" periodId={ID} finish={finishDetail} open={openNewDetails} period={initialValues} />
            }
            {
              selectedDetail &&
              <Details title="Editar detalle" periodId={ID} id={selectedDetail.id} finish={finishDetail} open={openNewDetails} period={initialValues} />
            }
          </div>
        }
        {/* <Button disabled={!ID} onClick={onClickNewDetail} type="primary" style={{marginBottom: 10}}>Agregar nuevo detalle</Button> */}
        {
          dataDetails.length === 0 ? <Empty /> :
            <Table dataSource={filteredDetails} columns={columns} />
        }
      </>
    </Drawer>
  );
};
export default App;